import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { BookOpen, Plus, Pencil, Trash2, Eye, EyeOff, Calendar, User2 } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", category: "معلومات عامة", body: "", cover_url: "", published: true };

export default function ArticlesAdminPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/articles"); setItems(data); }
    catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/articles/${editing.id}`, form);
      else await api.post("/articles", form);
      toast.success(editing ? "تم التحديث" : "تم النشر");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const startEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title || "", category: a.category || "", body: a.body || "",
      cover_url: a.cover_url || "", published: !!a.published,
    });
    setOpen(true);
  };

  const del = async (a) => {
    if (!confirm("حذف المقال نهائياً؟")) return;
    try { await api.delete(`/articles/${a.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const togglePublish = async (a) => {
    try {
      await api.patch(`/articles/${a.id}`, {
        title: a.title, category: a.category || "", body: a.body,
        cover_url: a.cover_url || "", published: !a.published,
      });
      toast.success(a.published ? "تم إلغاء النشر" : "تم النشر");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="articles-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-emerald-600" />
            المقالات والموضوعات العلمية
          </h1>
          <p className="text-slate-500 mt-2">أضف واعرض محتوى تربوي وتخصصي على الصفحة الرئيسية</p>
        </div>
        <Button
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
          className="h-12 gap-2 bg-emerald-600 hover:bg-emerald-700"
          data-testid="new-article-btn"
        >
          <Plus className="w-4 h-4" /> مقال جديد
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 ? (
          <Card className="md:col-span-2 lg:col-span-3"><CardContent className="p-16 text-center text-slate-400">لا توجد مقالات بعد</CardContent></Card>
        ) : items.map((a) => (
          <Card key={a.id} className="card-elevated border-slate-200/70 overflow-hidden" data-testid={`article-${a.id}`}>
            {a.cover_url ? (
              <div className="h-40 overflow-hidden bg-slate-100">
                <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="h-40 bg-gradient-to-br from-sky-100 via-emerald-100 to-amber-100 flex items-center justify-center">
                <BookOpen className="w-14 h-14 text-slate-400 opacity-50" />
              </div>
            )}
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                {a.category && <span className="text-xs font-black text-sky-700 uppercase">{a.category}</span>}
                <span className={`inline-flex items-center gap-1 text-[10px] font-black rounded-full px-2 py-0.5 ${a.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {a.published ? <><Eye className="w-3 h-3" /> منشور</> : <><EyeOff className="w-3 h-3" /> مسودة</>}
                </span>
              </div>
              <h3 className="mt-2 font-black text-slate-900 leading-tight line-clamp-2">{a.title}</h3>
              <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.body}</p>
              <div className="mt-3 text-[11px] text-slate-400 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><User2 className="w-3 h-3" />{a.author_name}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{(a.created_at || "").slice(0, 10)}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => startEdit(a)} className="gap-1.5" data-testid={`edit-article-${a.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> تعديل
                </Button>
                <Button variant="outline" size="sm" onClick={() => togglePublish(a)} className={`gap-1.5 ${a.published ? "text-amber-700 border-amber-200" : "text-emerald-700 border-emerald-200"}`}>
                  {a.published ? <><EyeOff className="w-3.5 h-3.5" /> إلغاء نشر</> : <><Eye className="w-3.5 h-3.5" /> نشر</>}
                </Button>
                <Button variant="outline" size="sm" onClick={() => del(a)} className="gap-1.5 text-rose-700 border-rose-200">
                  <Trash2 className="w-3.5 h-3.5" /> حذف
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader><DialogTitle className="text-2xl font-black">{editing ? "تعديل مقال" : "مقال جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="font-bold">العنوان</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="a-title" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-bold">التصنيف</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="تخاطب، سلوكي، تربية، صحة نفسية..." data-testid="a-category" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-bold">رابط صورة الغلاف (اختياري)</Label>
              <Input type="url" dir="ltr" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="https://..." data-testid="a-cover" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-bold">المحتوى</Label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={9} placeholder="اكتب هنا محتوى المقال..." data-testid="a-body" />
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <div className="font-bold text-slate-800">النشر مباشرة</div>
                <div className="text-xs text-slate-500">سيظهر في الصفحة الرئيسية فوراً</div>
              </div>
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="a-published" />
            </div>
            <Button onClick={save} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="save-article-btn">
              <BookOpen className="w-4 h-4" /> {editing ? "تحديث" : "نشر"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
